// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// A máquina de estados do pedido — D-009
// ============================================================================
//
// 📗 A GVA aceitou os 9 estados em 2026-08-06 com uma ressalva que é a parte importante
//    da resposta: *«parece-me perfeito, precisam é de ter significado prático»*.
//
// ⚠️ **E TINHA RAZÃO CONTRA O QUE ESTAVA FEITO.** Os estados existiam na base, com um
//    `trigger` a impor as transições, e no ecrã **não faziam nada**: o menu só sabia
//    cancelar. Um estado que ninguém pode pôr é decoração, e os planeadores usariam dois
//    ou três — que é exactamente o que a ressalva do cliente antecipou.
//
// ⚠️ **DUAS FONTES DE VERDADE, E É DELIBERADO — com um teste a segurá-las.** A tabela
//    abaixo é igual à do `trigger` da migração 010. Não podia ser uma só: a base não sabe
//    desenhar um menu, e o ecrã não pode ser o guardião (há três caminhos de escrita — o
//    ecrã, a efectivação, e o futuro leitor de email da fonte 5.3.1). **Quem MANDA é a
//    base**; isto serve só para não oferecer o que ela vai recusar. Um teste lê o SQL e
//    compara as duas, para não divergirem em silêncio.
// ============================================================================
/**
 * Para onde se pode ir a partir de cada estado.
 *
 * ⚠️ **De `executado` e de `cancelado` NÃO SE SAI.** Reabrir um pedido cancelado apagaria
 * o registo de que foi cancelado — e é isso que a auditoria existe para não deixar
 * acontecer. Um serviço que já aconteceu corrige-se, não se cancela: é outra operação.
 */
export const TRANSICOES = {
    // ⚠️ 098 · Converter é ir a `novo` — «validado e pronto a planear» —, e não a `confirmado`:
    //    `confirmado` é DERIVADO das alocações (ver `transicoesOferecidas`), e um pedido sem
    //    autocarro nenhum não está confirmado. O cliente disse «confirmado» no sentido do negócio
    //    (o cliente aceitou o preço); no sistema, isso é um pedido pronto a planear.
    orcamento: ['novo', 'cancelado'],
    recebido: ['em_validacao', 'cancelado'],
    em_validacao: ['novo', 'aguarda_informacao', 'aguarda_aprovacao', 'cancelado'],
    aguarda_informacao: ['em_validacao', 'cancelado'],
    aguarda_aprovacao: ['novo', 'em_validacao', 'cancelado'],
    novo: ['reservado', 'em_validacao', 'cancelado'],
    reservado: ['confirmado', 'novo', 'cancelado'],
    confirmado: ['executado', 'reservado', 'cancelado'],
    executado: [],
    cancelado: [],
};
/**
 * O que cada estado quer dizer **operacionalmente** — a ressalva do cliente.
 *
 * ⚠️ A distinção que torna os dois «aguarda» úteis em vez de sinónimos: um espera por
 * **FORA** (o cliente, 5.3.2.2) e o outro por **DENTRO** (uma aprovação nossa, 5.3.2.3).
 * São filas de trabalho diferentes, com pessoas diferentes a desbloqueá-las — e sem essa
 * diferença escrita, ninguém saberia qual escolher.
 */
export const SIGNIFICADO = {
    orcamento: 'pediram preço — não entra no planeamento até ser convertido em pedido',
    recebido: 'chegou, ninguém olhou ainda',
    em_validacao: 'alguém está a olhar agora',
    aguarda_informacao: 'parado à espera do CLIENTE',
    aguarda_aprovacao: 'parado à espera de uma aprovação NOSSA',
    novo: 'validado e pronto a planear',
    reservado: 'tem lugar no mapa, ainda se pode mexer',
    confirmado: 'os intervenientes foram avisados; mexer tem custo',
    executado: 'aconteceu — é histórico',
    cancelado: 'não vai acontecer',
};
/**
 * ⚠️⚠️ **QUÃO PRONTO ESTÁ ESTE PEDIDO PARA SER PLANEADO** — e é isto que dá consequência
 * prática aos estados.
 *
 * 📗 Cliente, 2026-08-08: *«quero que os estados comecem a fazer sentido, actualmente não
 * servem para nada»*. Tinha razão, e a prova estava escrita aqui ao lado: `SIGNIFICADO.novo`
 * dizia *«é o único que entra na fila»* e a `filaDeTrabalho` aceitava **cinco** estados sem
 * distinguir nenhum. Um pedido parado à espera do cliente aparecia ao lado de um pronto a
 * planear, com o mesmo ar e o mesmo botão.
 *
 * 📌 **É a família de D-4 outra vez: o comentário a afirmar o contrário do que o código faz.**
 *
 * ⚠️ **ORDENA, NÃO ESCONDE.** A tentação era tirar da fila o que está bloqueado — e isso
 * fazia desaparecer trabalho que alguém tem de destrancar. Um pedido à espera do cliente há
 * três semanas é **mais** urgente e não menos; o que muda é **quem** age a seguir.
 *
 * ⚠️ **E NÃO IMPEDE de planear.** Planear algo por que se espera informação é normalmente um
 * erro, mas é uma decisão da operação e não uma regra que a fonte tenha dado. Avisar é
 * nosso; impedir seria invenção — e o sistema que impede produz um caderno ao lado.
 *
 * Maior = mais pronto. Só se usa para desempatar dentro da fila.
 */
export const PRONTIDAO = {
    novo: 4, // validado: é o que se planeia
    em_validacao: 3, // alguém está a olhar agora
    recebido: 2, // chegou e ninguém olhou — falta o primeiro gesto, que é NOSSO
    aguarda_aprovacao: 1, // parado por DENTRO: a chave está cá
    aguarda_informacao: 0, // parado por FORA: depende de um telefonema ao cliente
    // ⚠️ 098 · Abaixo de tudo: um orçamento nem sequer é trabalho de planeamento ainda.
    orcamento: -1,
    // Os que já não estão na fila. Ficam declarados para o `Record` ser total: um mapa
    // parcial deixava um estado novo sem valor e a ordenação silenciosamente indefinida.
    reservado: 0,
    confirmado: 0,
    executado: 0,
    cancelado: 0,
};
/**
 * Os estados ANTES de `novo` — o que ainda não chegou à fila de planeamento.
 *
 * ⚠️⚠️ **NÃO é «tudo o que não se pode planear», e a diferença custou-me dois testes.**
 *
 *   A primeira versão desta separação usou `razaoParaNaoPlanear(p) !== null`, o que parecia
 *   elegante — uma regra só. Mas essa função recusa **quatro coisas diferentes**: quem espera
 *   por alguém, quem foi posto de lado (`inactivo`), quem tem dados em falta, e quem já
 *   acabou. 📌 Um pedido **inactivo** tem de ficar na fila **esbatido e etiquetado, nunca
 *   escondido** — é a mesma regra dos filtros do mapa, e havia um teste a segurá-la. Mandá-lo
 *   para outro separador **escondia-o**.
 *
 * ⚠️ E um `novo` **sem local de recolha** também fica na fila: falta-lhe um dado, e o sítio
 *   onde alguém o preenche é ao lado dos outros que se estão a planear — não numa gaveta.
 *
 * 📌 Uma só lista, aqui, usada pelo separador **e** pelo botão que desbloqueia: com duas, um
 *   estado novo entrava num lado e não no outro.
 */
export const ESTADOS_ANTES_DE_PLANEAR = [
    // ⚠️ 098 · O orçamento também está ANTES da fila: aparece no separador dos que esperam
    //    (com o gesto «Converter em pedido») e o conteúdo edita-se, como nos outros quatro.
    'orcamento',
    'recebido',
    'em_validacao',
    'aguarda_informacao',
    'aguarda_aprovacao',
];
/** `true` se o pedido ainda não chegou à fila de planeamento. */
export function antesDePlanear(estado) {
    return ESTADOS_ANTES_DE_PLANEAR.includes(estado);
}
/**
 * Os estados em que o CONTEÚDO do pedido ainda se edita livremente — itinerário, datas,
 * recolha, despejo, requisitos.
 *
 * 📗 Cliente, 2026-08-25, ao pedir para editar um pedido já criado: *«para já quero
 * apenas antes de reservado… modificar aí, não no mapa»*. `reservado` já tem lugar no
 * mapa (RF-7/D-007) — editar o pedido por baixo dele deixava o autocarro a sair para
 * uma recolha ou uma data que já não é a registada, sem tocar nas alocações.
 */
export const ESTADOS_ANTES_DE_RESERVAR = [
    ...ESTADOS_ANTES_DE_PLANEAR,
    'novo',
];
/** `true` se o conteúdo do pedido ainda se pode editar — antes de ter lugar no mapa. */
export function antesDeReservar(estado) {
    return ESTADOS_ANTES_DE_RESERVAR.includes(estado);
}
/**
 * ⚠️⚠️ **098 · O QUE NÃO ENTRA NO PLANEAMENTO, e porquê — UMA SÓ FORMULAÇÃO (regra 8).**
 *
 * 📗 Cliente, 2026-10-08: *«Orçamento: estado próprio — grava-se como pedido com estado
 * «orçamento»: não entra no planeamento até alguém o converter em confirmado.»*
 *
 * 📌 Devolve a RAZÃO (a frase que o ecrã mostra e que a reserva devolve no 409) ou `null`.
 * Quem pergunta «este pedido pode ser planeado?» — o «Planear», o arrasto, o encaixe, o
 * encher-buracos, a fila «Precisa de mim», a reserva no servidor — passa por aqui ou por
 * `ESTADOS_POR_PLANEAR`, que é derivada desta. Uma segunda cópia da lista divergiria na
 * primeira vez que alguém acrescentasse um estado. Guardado por `orcamento-fora-do-planeamento.test.ts`.
 *
 * ⚠️ **Não é `antesDePlanear`:** os quatro estados de validação também ainda não se planeiam,
 * mas ENTRAM no planeamento (o encaixe propõe-nos, a fila conta-os). O orçamento não entra em
 * nenhum desses sítios.
 */
export function foraDoPlaneamento(estado) {
    if (estado === 'orcamento')
        return 'é um orçamento — converta-o em pedido para planear';
    return null;
}
/**
 * Os estados que AINDA NÃO TÊM LUGAR NO MAPA e já entram no planeamento — o que o encaixe, o
 * encher-buracos e a fila de trabalho do servidor consideram «por planear».
 *
 * 📌 Derivada e não escrita: são os de `ESTADOS_ANTES_DE_RESERVAR` menos o que
 * `foraDoPlaneamento` recusa. Era a lista `PLANEAVEIS` do `encaixe.ts`, escrita à mão.
 */
export const ESTADOS_POR_PLANEAR = ESTADOS_ANTES_DE_RESERVAR.filter((e) => foraDoPlaneamento(e) === null);
/**
 * Está à espera de alguém — e de quem?
 *
 * `null` quer dizer «não está bloqueado». É o que a lista escreve na linha, porque «à espera
 * do cliente» é accionável e «aguarda_informacao» é uma etiqueta.
 */
export function aEsperaDe(estado) {
    if (estado === 'aguarda_informacao')
        return 'cliente';
    if (estado === 'aguarda_aprovacao')
        return 'aprovacao';
    return null;
}
/**
 * As transições que o ecrã pode oferecer a partir do estado actual.
 *
 * ⚠️ **`reservado`, `confirmado` e `executado` saem da lista do menu**, e não por serem
 * ilegais: são **derivados do plano**. Pôr «passar a reservado» num menu era prometer uma
 * escrita que a coluna aceita e o mapa desmente — o estado que a lista mostra vem das
 * alocações, e sem alocações ele volta a `novo` no primeiro recarregamento.
 *
 * Quem os produz é o gesto certo: reservar no mapa, efectivar, e a data passar.
 */
export function transicoesOferecidas(actual) {
    const derivados = new Set(['reservado', 'confirmado', 'executado']);
    return (TRANSICOES[actual] ?? []).filter((e) => !derivados.has(e));
}
//# sourceMappingURL=pedido-estados.js.map